import { getItemLocations, unlinkItem, unlinkItems } from "../../utils/requests/itemRequests";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import type { LinkedBoard, TableDataInterface } from "../ItemViewSection/table.data.interface";
import logger from "../../lib/logger";

/** Same ceiling the batch add view enforces on a selection. */
export const MAX_BATCH_UNLINK_ITEMS = 100;

/**
 * Requests in flight at once. Each sync lookup fans out into several Monday
 * queries on the server, so a full selection must not be fired all at once.
 */
const REQUEST_CONCURRENCY = 5;

export type UnlinkCandidateStatus = "synced" | "not_synced" | "check_failed";

export interface UnlinkCandidate {
  itemId: string;
  name: string;
  status: UnlinkCandidateStatus;
  /** Board SIMB has this item recorded on — the one the unlink request must name. */
  boardId: string | null;
  /** Names of the other boards this item is synced with. */
  connectedBoards: string[];
}

export interface UnlinkResult {
  queued: string[];
  failed: string[];
}

export async function mapWithConcurrency<T, R>(
  values: T[],
  limit: number,
  worker: (value: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(values.length);
  let next = 0;

  const run = async () => {
    while (next < values.length) {
      const index = next++;
      results[index] = await worker(values[index], index);
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, values.length) }, run));
  return results;
}

/**
 * `linkedBoards` holds every member of the item's sync group, the item itself
 * included. The item's own row carries the board the unlink must target; the
 * remaining rows are the boards it is connected to.
 */
export function buildCandidate(
  itemId: string,
  name: string,
  locations: TableDataInterface | null,
  currentBoard: number | string | null,
): UnlinkCandidate {
  const linkedBoards: LinkedBoard[] = locations?.linkedBoards ?? [];

  if (!locations || locations.noLinkedItems || linkedBoards.length === 0) {
    return { itemId, name, status: "not_synced", boardId: null, connectedBoards: [] };
  }

  const own = linkedBoards.find((board) => String(board.itemId) === itemId);
  const ownBoardId = own?.boardId ?? currentBoard;
  const connectedBoards = linkedBoards
    .filter((board) => String(board.itemId) !== itemId)
    .map((board) => board.boardName || `Board ${board.boardId}`);

  return {
    itemId,
    name,
    status: "synced",
    boardId: ownBoardId != null ? String(ownBoardId) : null,
    connectedBoards: [...new Set(connectedBoards)],
  };
}

async function fetchItemNames(itemIds: string[]): Promise<Record<string, string>> {
  try {
    const response: any = await MondayApiQueryInstance.getItemNames(itemIds.map(Number));
    const names: Record<string, string> = {};
    for (const item of response?.data?.items ?? []) {
      names[String(item.id)] = item.name;
    }
    return names;
  } catch (error: any) {
    // Names are display-only; the view falls back to the item id.
    logger.error(error?.message);
    return {};
  }
}

export async function loadUnlinkCandidates(
  token: string | null,
  itemIds: Array<number | string>,
  currentBoard: number | null,
  userId: number | string | null,
): Promise<UnlinkCandidate[]> {
  const ids = [...new Set(itemIds.map(String))];
  const names = await fetchItemNames(ids);

  return mapWithConcurrency(ids, REQUEST_CONCURRENCY, async (itemId) => {
    const name = names[itemId] ?? `Item ${itemId}`;
    try {
      const locations = await getItemLocations(token, [Number(itemId)], currentBoard, userId);
      return buildCandidate(itemId, name, locations, currentBoard);
    } catch {
      return {
        itemId,
        name,
        status: "check_failed" as const,
        boardId: null,
        connectedBoards: [],
      };
    }
  });
}

/**
 * Queues one unlink per item. The request only confirms the job was queued —
 * the unlink itself completes in the background.
 */
export async function unlinkCandidates(
  token: string,
  candidates: UnlinkCandidate[],
  onProgress?: (completed: number) => void,
): Promise<UnlinkResult> {
  const result: UnlinkResult = { queued: [], failed: [] };
  let completed = 0;

  await mapWithConcurrency(candidates, REQUEST_CONCURRENCY, async (candidate) => {
    try {
      if (!candidate.boardId) throw new Error("Missing board for item");
      await unlinkItem(token, Number(candidate.itemId), Number(candidate.boardId));
      result.queued.push(candidate.itemId);
    } catch {
      result.failed.push(candidate.itemId);
    }
    onProgress?.(++completed);
  });

  return result;
}

/** Body code of the 409 simb-backend answers while the orchestrated unlink is switched off. */
export const UNLINK_ORCHESTRATION_DISABLED_CODE = "UNLINK_ORCHESTRATION_DISABLED";

export interface BatchUnlinkStart {
  /**
   * `orchestrated`: one request, and every item's outcome comes back keyed on `requestId`.
   * `legacy`: one request per item, whose outcomes come back without an item id.
   */
  mode: "orchestrated" | "legacy";
  requestId: string | null;
  queued: string[];
  failed: string[];
}

const orchestrationUnavailable = (error: any): boolean => {
  const status = error?.response?.status;
  // 404: a backend that predates the endpoint. Same answer as the flag being off.
  return (
    status === 404 ||
    (status === 409 && error?.response?.data?.code === UNLINK_ORCHESTRATION_DISABLED_CODE)
  );
};

/**
 * Unlinks the selection through orchestration, falling back to the legacy per-item requests
 * when the backend says that path is not available.
 */
export async function startBatchUnlink(
  token: string,
  candidates: UnlinkCandidate[],
  onProgress?: (completed: number) => void,
): Promise<BatchUnlinkStart> {
  const itemIds = candidates.map((candidate) => candidate.itemId);

  try {
    const { requestId } = await unlinkItems(token, itemIds);
    onProgress?.(itemIds.length);
    return { mode: "orchestrated", requestId, queued: itemIds, failed: [] };
  } catch (error: any) {
    if (!orchestrationUnavailable(error)) {
      logger.error(error?.message);
      return { mode: "orchestrated", requestId: null, queued: [], failed: itemIds };
    }
  }

  const legacy = await unlinkCandidates(token, candidates, onProgress);
  return { mode: "legacy", requestId: null, ...legacy };
}
